"use client";

import { TypeBannerSkeleton } from "@contentful/types";
import { faCircleX } from "@fortawesome/pro-light-svg-icons";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons/faArrowRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCountdown } from "@yardzen/hooks";
import { cva, type VariantProps } from "class-variance-authority";
import { Entry } from "contentful";
import * as React from "react";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { B } from "./b";
import { Button } from "./button";
import { Small } from "./small";

const bannerVariants = cva("", {
  variants: {
    variant: {
      announcement: "bg-brand-tertiary-30",
      promo: "bg-brand-secondary-dark text-white",
    },
  },
});

type BannerProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof bannerVariants> & {
    bannerCopy?: string | null | undefined;
    bannerLink?: string | null | undefined;
    promoCode?: string | null | undefined;
    bannerContent: Entry<
      TypeBannerSkeleton,
      "WITHOUT_UNRESOLVABLE_LINKS",
      string
    > | null;
    onBannerClose: (variant: "announcement" | "promo") => void;
  };

export function Banner({
  promoCode,
  variant,
  bannerContent,
  onBannerClose,
  ...props
}: BannerProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { code, endTime } = JSON.parse(promoCode || "{}");
  const [showCopied, setShowCopied] = useState(false);
  // endTime can be either:
  // - A Unix timestamp in seconds (legacy format, e.g., 1768607999)
  // - An ISO date string (new format, e.g., "2026-01-16T23:59:59.000Z")
  const endTimeSeconds = endTime
    ? typeof endTime === "number"
      ? endTime // Already in seconds
      : Math.floor(new Date(endTime).getTime() / 1000) // ISO string → seconds
    : undefined;
  const countdown = useCountdown(endTimeSeconds);
  const bannerCopy = bannerContent?.fields.copy?.toString() || "";
  const bannerLink = bannerContent?.fields.url?.toString() || "";
  const redirectText = bannerContent?.fields.redirectText?.toString() || "";
  const promotionalCode =
    bannerContent?.fields.promotionalCode?.toString() || "";
  const welcomeCodeBeforeCode =
    bannerContent?.fields.welcomeCodeBeforeCode?.toString() || "";
  const welcomeCodeAfterCode =
    bannerContent?.fields.welcomeCodeAfterCode?.toString() || "";
  if (!isClient) return null;
  const welcomeCodeAfterCodeReplace = modifyWelcomeCodeAmount(
    code,
    welcomeCodeAfterCode,
  );

  function bannerClick() {
    if (variant === "announcement") {
      // No promo code to copy → skip the "copied" pause and just redirect.
      if (promotionalCode.length === 0) {
        window.location.href = bannerLink;
        return;
      }
      navigator.clipboard
        .writeText(promotionalCode)
        .then(() => {
          setShowCopied(true);
          setTimeout(() => {
            setShowCopied(false);
            window.location.href = bannerLink;
          }, 1000);
        })
        .catch((err) => {
          console.error("Failed to copy promo code:", err);
        });
    } else if (variant === "promo") {
      navigator.clipboard
        .writeText(code || "")
        .then(() => {
          setShowCopied(true);
          setTimeout(() => {
            setShowCopied(false);
          }, 1000);
        })
        .catch((err) => {
          console.error("Failed to copy welcome promo code:", err);
        });
    }
  }

  return (
    <div id="yz-banner" className="sticky top-0 z-50 w-full">
      <div
        className="cursor-pointer"
        onClick={() => {
          bannerClick();
        }}
      >
        <div
          className={cn(
            bannerVariants({ variant: variant }),
            "flex w-full flex-row items-center justify-center px-12 py-1 text-center ",
          )}
        >
          {variant === "announcement" && (
            <div className="flex flex-row items-center justify-center gap-2 text-center">
              {showCopied && promotionalCode.length > 0 ? (
                <div className="flex flex-row items-center justify-center gap-2">
                  <small>
                    <B>{redirectText || "Promo Code Copied"}</B>
                  </small>
                </div>
              ) : (
                <>
                  <small>{bannerCopy}</small>
                  {bannerLink && bannerCopy && (
                    <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                  )}
                </>
              )}
            </div>
          )}
          {variant === "promo" && (
            <div className="flex flex-row items-center justify-center gap-2 text-center">
              {showCopied ? (
                <div className="flex min-h-6 flex-row items-center justify-center gap-2">
                  <Small>
                    <B>Promo Code Copied</B>
                  </Small>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
                  <Small>
                    {welcomeCodeBeforeCode} <B>{code}</B>{" "}
                    {welcomeCodeAfterCodeReplace}
                  </Small>
                  <div className="h-6 w-28 rounded-md bg-white px-2 text-black">
                    <small>{countdown}</small>
                  </div>
                </div>
              )}
            </div>
          )}

          <Button
            size="icon"
            variant="ghost"
            className="absolute right-0 mr-0 hover:h-6 sm:mr-4"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBannerClose(variant ?? "announcement");
            }}
          >
            <FontAwesomeIcon
              size="lg"
              icon={faCircleX}
              color={variant === "promo" ? "white" : "black"}
            />
          </Button>
        </div>
      </div>
    </div>
  );

  function modifyWelcomeCodeAmount(code: string, copy: string | undefined) {
    if (!copy) return "";
    if (!code) return "";
    // if the previous promo code was WELCOME10, we need to honor that value
    if (code.startsWith("WELCOME10")) {
      return copy.replace("15%", "10%");
    } else {
      return copy;
    }
  }
}
